import { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import { Mic, Volume2, Bell, Loader2, CheckCircle2, XCircle } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import useVolumeDoAlerta from "../../hooks/useVolumeDoAlerta";
import { criarToque } from "../../ligacoes/audio";
import { CHAVE_MICROFONE, microfoneEscolhido } from "../../ligacoes/store";

// Ajustes deste navegador: qual microfone usar, testar se ele capta, ouvir o
// toque e permitir o aviso com a aba em segundo plano. Nada disso vai para o
// servidor — é da máquina de quem atende.
const EsteComputador = () => {
	const { volume } = useVolumeDoAlerta();
	const [microfones, setMicrofones] = useState([]);
	const [escolhido, setEscolhido] = useState(microfoneEscolhido() || "padrao");
	const [testando, setTestando] = useState(false);
	const [nivel, setNivel] = useState(0);
	const [permissaoMic, setPermissaoMic] = useState("desconhecida");
	const [notif, setNotif] = useState(window.Notification?.permission || "indisponivel");
	const teste = useRef(null);
	const toque = useRef(null);

	const listar = async () => {
		try {
			const todos = await navigator.mediaDevices.enumerateDevices();
			setMicrofones(todos.filter((d) => d.kind === "audioinput"));
		} catch {
			setMicrofones([]);
		}
	};

	useEffect(() => {
		listar();
		navigator.permissions
			?.query({ name: "microphone" })
			.then((p) => {
				setPermissaoMic(p.state);
				p.onchange = () => setPermissaoMic(p.state);
			})
			.catch(() => {});
		navigator.mediaDevices?.addEventListener?.("devicechange", listar);
		return () => {
			navigator.mediaDevices?.removeEventListener?.("devicechange", listar);
			pararTeste();
			toque.current?.parar();
		};
	}, []);

	const escolher = (v) => {
		setEscolhido(v);
		try {
			if (v === "padrao") localStorage.removeItem(CHAVE_MICROFONE);
			else localStorage.setItem(CHAVE_MICROFONE, v);
		} catch {
			// sem storage
		}
		if (testando) {
			pararTeste();
			iniciarTeste(v);
		}
	};

	function pararTeste() {
		if (!teste.current) return;
		cancelAnimationFrame(teste.current.raf);
		teste.current.stream.getTracks().forEach((t) => t.stop());
		teste.current.ctx.close().catch(() => {});
		teste.current = null;
		setTestando(false);
		setNivel(0);
	}

	async function iniciarTeste(id = escolhido) {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: id && id !== "padrao" ? { deviceId: { exact: id } } : true,
			});
			const ctx = new (window.AudioContext || window.webkitAudioContext)();
			const an = ctx.createAnalyser();
			an.fftSize = 512;
			ctx.createMediaStreamSource(stream).connect(an);
			const dados = new Uint8Array(an.frequencyBinCount);
			const loop = () => {
				an.getByteTimeDomainData(dados);
				let pico = 0;
				for (let i = 0; i < dados.length; i += 1) pico = Math.max(pico, Math.abs(dados[i] - 128));
				setNivel(pico / 128);
				teste.current.raf = requestAnimationFrame(loop);
			};
			teste.current = { stream, ctx, raf: 0 };
			setTestando(true);
			setPermissaoMic("granted");
			loop();
			listar(); // com permissão, os nomes dos microfones aparecem
		} catch (err) {
			setPermissaoMic("denied");
			toast.error(
				err?.name === "NotAllowedError"
					? "O navegador bloqueou o microfone. Libere no cadeado ao lado do endereço."
					: "Não foi possível abrir o microfone."
			);
		}
	}

	const ouvirToque = () => {
		if (!toque.current) toque.current = criarToque();
		toque.current.iniciar(volume ?? 0.5);
		setTimeout(() => toque.current?.parar(), 4500);
	};

	const pedirNotificacao = async () => {
		if (!window.Notification) return;
		const r = await Notification.requestPermission();
		setNotif(r);
		if (r === "granted") toast.success("Pronto: ligações avisam mesmo com a aba em segundo plano.");
	};

	const Estado = ({ ok, textoOk, textoNao }) =>
		ok ? (
			<span className="flex items-center gap-1 text-xs text-emerald-600">
				<CheckCircle2 className="h-3.5 w-3.5" /> {textoOk}
			</span>
		) : (
			<span className="flex items-center gap-1 text-xs text-muted-foreground">
				<XCircle className="h-3.5 w-3.5" /> {textoNao}
			</span>
		);

	return (
		<div className="max-w-3xl space-y-3">
			<div className="rounded-xl border bg-card p-4">
				<div className="flex items-start gap-3">
					<Mic className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1 space-y-3">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<p className="text-sm font-medium">Microfone</p>
							<Estado
								ok={permissaoMic === "granted"}
								textoOk="Permitido neste navegador"
								textoNao={permissaoMic === "denied" ? "Bloqueado pelo navegador" : "Ainda não permitido"}
							/>
						</div>
						<Select value={escolhido} onValueChange={escolher}>
							<SelectTrigger className="max-w-md">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="padrao">Padrão do sistema</SelectItem>
								{microfones
									.filter((m) => m.deviceId && m.deviceId !== "default")
									.map((m, i) => (
										<SelectItem key={m.deviceId} value={m.deviceId}>
											{m.label || `Microfone ${i + 1}`}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						<div className="flex items-center gap-3">
							<Button size="sm" variant={testando ? "secondary" : "outline"} onClick={() => (testando ? pararTeste() : iniciarTeste())}>
								{testando ? "Parar teste" : "Testar microfone"}
							</Button>
							<div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
								<div
									className="h-full bg-emerald-500 transition-[width] duration-75"
									style={{ width: `${Math.min(100, nivel * 250)}%` }}
								/>
							</div>
						</div>
						<p className="text-xs text-muted-foreground">
							Fale algo: a barra tem que mexer. Vale só para este computador.
						</p>
					</div>
				</div>
			</div>

			<div className="rounded-xl border bg-card p-4">
				<div className="flex items-start gap-3">
					<Volume2 className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1 space-y-2">
						<p className="text-sm font-medium">Toque de chamada</p>
						<p className="text-xs text-muted-foreground">
							Segue o volume de alertas (ícone de alto-falante no topo). A voz da ligação sai no alto-falante padrão do
							sistema.
						</p>
						<Button size="sm" variant="outline" onClick={ouvirToque}>
							Ouvir o toque
						</Button>
					</div>
				</div>
			</div>

			<div className="rounded-xl border bg-card p-4">
				<div className="flex items-start gap-3">
					<Bell className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1 space-y-2">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<p className="text-sm font-medium">Aviso com a aba em segundo plano</p>
							<Estado
								ok={notif === "granted"}
								textoOk="Ativado"
								textoNao={notif === "denied" ? "Bloqueado pelo navegador" : notif === "indisponivel" ? "Navegador sem suporte" : "Desativado"}
							/>
						</div>
						<p className="text-xs text-muted-foreground">
							Mostra “Ligação recebida” no canto da tela mesmo com o sistema minimizado ou em outra aba.
						</p>
						{notif !== "granted" && notif !== "indisponivel" && (
							<Button size="sm" variant="outline" disabled={notif === "denied"} onClick={pedirNotificacao}>
								{notif === "denied" ? "Libere no cadeado ao lado do endereço" : "Ativar aviso"}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default EsteComputador;
